import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingApi, BillingMilestone } from '../api/billing';
import PremiumLayout from '../components/PremiumLayout';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, Currency } from '../utils/currencyUtils';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const Billing: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<BillingMilestone | null>(null);
  const [formData, setFormData] = useState({
    lead_id: '',
    name: '',
    amount: '',
    currency: 'INR' as 'USD' | 'INR', // Always INR - currency selector removed
    due_date: '',
    status: 'pending' as 'pending' | 'paid' | 'overdue' | 'cancelled',
  });

  const queryClient = useQueryClient();

  const { data: milestones = [], isLoading } = useQuery({
    queryKey: ['billing'],
    queryFn: () => billingApi.getAll(),
  });

  const { data: statistics } = useQuery({
    queryKey: ['billing-statistics'],
    queryFn: () => billingApi.getStatistics(),
  });

  const createMutation = useMutation({
    mutationFn: billingApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      setShowModal(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<BillingMilestone> }) =>
      billingApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      setShowModal(false);
      setEditingMilestone(null);
      resetForm();
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: ({ id, amount }: { id: number; amount: number }) =>
      billingApi.markAsPaid(id, amount),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: billingApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing'] });
    },
  });

  const resetForm = () => {
    setFormData({
      lead_id: '',
      name: '',
      amount: '',
      currency: 'INR' as 'USD' | 'INR',
      due_date: '',
      status: 'pending',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const milestoneData = {
      ...formData,
      lead_id: parseInt(formData.lead_id),
      amount: parseFloat(formData.amount),
      currency: formData.currency, // Store the currency in which amount was entered
    };

    if (editingMilestone) {
      updateMutation.mutate({ id: editingMilestone.id, data: milestoneData });
    } else {
      createMutation.mutate(milestoneData);
    }
  };

  const handleEdit = (milestone: BillingMilestone) => {
    setEditingMilestone(milestone);
    setFormData({
      lead_id: milestone.lead_id.toString(),
      name: milestone.name,
      amount: milestone.amount.toString(),
      currency: (milestone.currency || 'INR') as 'USD' | 'INR',
      due_date: milestone.due_date.split('T')[0],
      status: milestone.status,
    });
    setShowModal(true);
  };

  const handleMarkPaid = (id: number, amount: number) => {
    const paidAmount = prompt('Enter paid amount:', amount.toString());
    if (paidAmount) {
      markPaidMutation.mutate({ id, amount: parseFloat(paidAmount) });
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this billing milestone?')) {
      deleteMutation.mutate(id);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'overdue':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  if (isLoading) {
    return (
      <PremiumLayout title="Billing">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title="Billing Management"
      subtitle="Manage billing milestones and payments"
    >
      <div className="space-y-6">
        {/* Statistics */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="premium-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Pending</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(statistics.totalPending, 'INR')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">(Mixed currencies)</p>
                </div>
                <ClockIcon className="h-8 w-8 text-yellow-500" />
              </div>
            </div>
            <div className="premium-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Overdue</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(statistics.totalOverdue, 'INR')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">(Mixed currencies)</p>
                </div>
                <ExclamationTriangleIcon className="h-8 w-8 text-red-500" />
              </div>
            </div>
            <div className="premium-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Paid</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(statistics.totalPaid, 'INR')}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">(Mixed currencies)</p>
                </div>
                <CheckCircleIcon className="h-8 w-8 text-green-500" />
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end">
          <button
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Milestone
          </button>
        </div>

        {/* Milestones List */}
        <div className="premium-card p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Billing Milestones</h2>
          {milestones.length === 0 ? (
            <div className="text-center py-12">
              <CurrencyDollarIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No billing milestones found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {milestones.map((milestone) => (
                    <tr key={milestone.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {milestone.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(milestone.amount, 'INR')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(milestone.due_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(milestone.status)}
                          <span className="ml-2 text-sm text-gray-900 capitalize">{milestone.status}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        {milestone.status !== 'paid' && (
                          <button
                            onClick={() => handleMarkPaid(milestone.id, milestone.amount)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Mark Paid
                          </button>
                        )}
                        <button
                          onClick={() => handleEdit(milestone)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(milestone.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start justify-center z-50 pt-4 pb-4 px-4">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {editingMilestone ? 'Edit Milestone' : 'Create Milestone'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Lead ID
                  </label>
                  <input
                    type="number"
                    value={formData.lead_id}
                    onChange={(e) => setFormData({ ...formData, lead_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Amount (INR)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Enter amount in Indian Rupees (₹)
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="pending">Pending</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white py-2 px-4 rounded-lg hover:bg-indigo-700"
                  >
                    {editingMilestone ? 'Update' : 'Create'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingMilestone(null);
                      resetForm();
                    }}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default Billing;
